/**
 * SpawnBatchForm Component
 *
 * Dialog form for creating spawn batches - either from liquid cultures
 * or as direct stock entry (for stock takes).
 * Part of US-P2-002: Spawn Production Tracking
 * Enhanced for US-P7-001B: Variable Cost Foundation (Step 4)
 * Updated: Spawn Recipe selector replaces grain/bag/filter fields
 *
 * Features:
 * - Spawn Recipe selector (defines grain, water, bag size, LC volume, filters)
 * - Optional liquid culture selection (for normal production flow)
 * - Direct strain selection (for stock entry without LC)
 * - Bag count configuration
 * - Recipe summary with per-bag composition + totals
 * - Auto-suggest LC volume from recipe
 * - Real-time cost preview
 * - Cost fields saved on creation
 */

import { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useLiquidCultures, useStrains } from '@/hooks/useLC'
import { useLCMutations } from '@/hooks/useLCMutations'
import { useSpawnMutations } from '@/hooks/useSpawnBatches'
import { useSpawnBatchCostPreview } from '@/hooks/useSpawnBatchCost'
import { useAvailableSyringes, useCultureSyringes } from '@/hooks/useCultureSyringes'
import { useSpawnRecipes, useSpawnRecipeMutations } from '@/hooks/useSpawnRecipes'
import SpawnCostPreview from './SpawnCostPreview'
import SpawnRecipeModal from './SpawnRecipeModal'
import { AlertTriangle, Beaker, Plus, Syringe, TestTube } from 'lucide-react'
import { toLocalDateString } from '@/utils/dateUtils'

export default function SpawnBatchForm({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    code: '',
    source_type: 'lc', // 'lc' or 'syringe'
    liquid_culture_id: '',
    lc_volume_used_ml: '',
    source_syringe_id: '',
    syringe_volume_used_ml: '',
    strain_id: '',
    bag_count: 1,
    spawn_recipe_id: '',
    // Derived from recipe (used for cost preview + submit)
    grain_type: 'millet',
    bag_weight_kg: 2.5,
    use_filters: true,
    grain_weight_per_bag_kg: null,
    inoculation_date: toLocalDateString(new Date()),
    notes: ''
  })
  const [error, setError] = useState(null)
  const [selectedLC, setSelectedLC] = useState(null)
  const [selectedSyringe, setSelectedSyringe] = useState(null)
  const [recipeModalOpen, setRecipeModalOpen] = useState(false)

  // Fetch spawn recipes
  const { recipes, refetch: refetchRecipes } = useSpawnRecipes()
  const { createRecipe, loading: savingRecipe } = useSpawnRecipeMutations()

  // Fetch available liquid cultures (ready or in_use status)
  const { liquidCultures } = useLiquidCultures({
    status: 'ready'
  })
  const { liquidCultures: inUseCultures } = useLiquidCultures({
    status: 'in_use'
  })
  const { strains } = useStrains()
  const { createSpawnBatch, loading: saving } = useSpawnMutations()
  const { useSyringe } = useCultureSyringes()
  const { updateLC } = useLCMutations()

  // Fetch available syringes for selected strain (when syringe source is selected)
  const { syringes: availableSyringes } = useAvailableSyringes(
    formData.source_type === 'syringe' ? formData.strain_id : null
  )

  // Cost preview hook for real-time cost calculation
  const {
    grainCost,
    bagCost,
    filterCost,
    totalCost,
    costPerBag
  } = useSpawnBatchCostPreview(formData, { debounceMs: 300 })

  // Find selected recipe object
  const selectedRecipe = useMemo(() => {
    return recipes.find(r => r.id === formData.spawn_recipe_id) || null
  }, [recipes, formData.spawn_recipe_id])

  // Combine ready and in_use cultures (memoised to avoid re-renders)
  const availableCultures = useMemo(() => {
    return [...liquidCultures, ...inUseCultures]
  }, [liquidCultures, inUseCultures])

  // When recipe changes, derive form values from recipe
  useEffect(() => {
    if (selectedRecipe) {
      setFormData(prev => ({
        ...prev,
        grain_type: selectedRecipe.grain_type,
        bag_weight_kg: Number(selectedRecipe.bag_weight_kg),
        use_filters: selectedRecipe.use_filters,
        grain_weight_per_bag_kg: Number(selectedRecipe.grain_weight_kg)
      }))
    }
  }, [selectedRecipe])

  // Auto-suggest LC volume when recipe and bag count change
  useEffect(() => {
    if (selectedRecipe && formData.bag_count > 0 && formData.source_type === 'lc' && formData.liquid_culture_id) {
      const suggestedVolume = Number(selectedRecipe.lc_volume_ml) * parseInt(formData.bag_count)
      // Only auto-suggest if user hasn't manually entered a value
      if (!formData.lc_volume_used_ml) {
        setFormData(prev => ({
          ...prev,
          lc_volume_used_ml: suggestedVolume.toString()
        }))
      }
    }
  }, [selectedRecipe, formData.bag_count, formData.liquid_culture_id, formData.source_type])

  // Update strain when LC is selected
  useEffect(() => {
    if (formData.source_type === 'lc' && formData.liquid_culture_id) {
      const lc = availableCultures.find(c => c.id === formData.liquid_culture_id)
      if (lc) {
        setSelectedLC(lc)
        setFormData(prev => ({
          ...prev,
          strain_id: lc.strain_id
        }))
      }
    } else {
      setSelectedLC(null)
      // Don't clear strain_id - user may have selected it directly
    }
  }, [formData.liquid_culture_id, formData.source_type, availableCultures])

  // Update selected syringe state when syringe is selected
  useEffect(() => {
    if (formData.source_type === 'syringe' && formData.source_syringe_id) {
      const syringe = availableSyringes.find(s => s.id === formData.source_syringe_id)
      if (syringe) {
        setSelectedSyringe(syringe)
      }
    } else {
      setSelectedSyringe(null)
    }
  }, [formData.source_syringe_id, formData.source_type, availableSyringes])

  // Clear source selections when switching source type
  useEffect(() => {
    if (formData.source_type === 'lc') {
      setFormData(prev => ({
        ...prev,
        source_syringe_id: '',
        syringe_volume_used_ml: ''
      }))
      setSelectedSyringe(null)
    } else if (formData.source_type === 'syringe') {
      setFormData(prev => ({
        ...prev,
        liquid_culture_id: '',
        lc_volume_used_ml: ''
      }))
      setSelectedLC(null)
    }
  }, [formData.source_type])

  // Clear LC selection when strain changes if LC doesn't match new strain
  useEffect(() => {
    if (formData.strain_id && formData.liquid_culture_id) {
      const selectedCulture = availableCultures.find(lc => lc.id === formData.liquid_culture_id)
      if (selectedCulture && selectedCulture.strain_id !== formData.strain_id) {
        setFormData(prev => ({
          ...prev,
          liquid_culture_id: '',
          lc_volume_used_ml: ''
        }))
        setSelectedLC(null)
      }
    }
  }, [formData.strain_id, formData.liquid_culture_id, availableCultures])

  // Parse batch code to extract date and strain (format: YYMMDD[STRAIN CODE])
  useEffect(() => {
    if (formData.code && formData.code.length >= 6) {
      const dateStr = formData.code.substring(0, 6)
      const strainCode = formData.code.substring(6).toUpperCase()

      // Parse YYMMDD
      const year = parseInt(dateStr.substring(0, 2), 10)
      const month = parseInt(dateStr.substring(2, 4), 10)
      const day = parseInt(dateStr.substring(4, 6), 10)

      // Validate date parts
      if (!isNaN(year) && !isNaN(month) && !isNaN(day) &&
          month >= 1 && month <= 12 && day >= 1 && day <= 31) {
        const fullYear = 2000 + year
        const isoDate = `${fullYear}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`

        // Only update if date is valid
        const testDate = new Date(isoDate)
        if (!isNaN(testDate.getTime())) {
          setFormData(prev => ({
            ...prev,
            inoculation_date: isoDate
          }))
        }
      }

      // Try to match strain by code
      if (strainCode && strains?.length > 0) {
        const matchedStrain = strains.find(s =>
          s.code?.toUpperCase() === strainCode
        )
        if (matchedStrain && !formData.liquid_culture_id) {
          setFormData(prev => ({
            ...prev,
            strain_id: matchedStrain.id
          }))
        }
      }
    }
  }, [formData.code, strains, formData.liquid_culture_id])

  // Calculate expected ready date (14 days from inoculation)
  const expectedReadyDate = new Date(formData.inoculation_date)
  expectedReadyDate.setDate(expectedReadyDate.getDate() + 14)

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setError(null)
  }

  // Handle recipe selection (including "add new" option)
  const handleRecipeChange = (value) => {
    if (value === '__new__') {
      setRecipeModalOpen(true)
      return
    }
    // Clear the auto-suggested LC volume when recipe changes
    setFormData(prev => ({
      ...prev,
      spawn_recipe_id: value === 'none' ? '' : value,
      lc_volume_used_ml: ''
    }))
    setError(null)
  }

  // Handle new recipe creation
  const handleCreateRecipe = async (recipeData) => {
    try {
      const newRecipe = await createRecipe(recipeData)
      await refetchRecipes()
      setRecipeModalOpen(false)
      // Auto-select the new recipe
      handleChange('spawn_recipe_id', newRecipe.id)
    } catch (err) {
      // Error is handled in the hook
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validation - strain is required (either from LC, syringe, or direct selection)
    if (!formData.strain_id) {
      setError('Please select a strain')
      return
    }

    // Validation - recipe is required
    if (!formData.spawn_recipe_id) {
      setError('Please select a spawn recipe')
      return
    }

    // Validation for syringe source
    if (formData.source_type === 'syringe') {
      if (!formData.source_syringe_id) {
        setError('Please select a syringe')
        return
      }
      const volumeUsed = parseFloat(formData.syringe_volume_used_ml)
      if (!volumeUsed || volumeUsed <= 0) {
        setError('Please enter volume used from syringe')
        return
      }
      if (selectedSyringe && volumeUsed > selectedSyringe.volume_remaining_ml) {
        setError(`Volume exceeds available (${selectedSyringe.volume_remaining_ml}ml)`)
        return
      }
    }

    // Validation for LC volume (optional but must be valid if entered)
    if (formData.source_type === 'lc' && formData.liquid_culture_id && formData.lc_volume_used_ml) {
      const volumeUsed = parseFloat(formData.lc_volume_used_ml)
      if (volumeUsed <= 0) {
        setError('Volume used must be greater than 0')
        return
      }
      const availableVolume = selectedLC?.volume_remaining_ml ?? selectedLC?.volume_ml
      if (selectedLC && volumeUsed > availableVolume) {
        setError(`Volume exceeds available (${availableVolume}ml)`)
        return
      }
    }

    if (formData.bag_count < 1) {
      setError('Bag count must be at least 1')
      return
    }

    try {
      // Calculate consumables cost (bags + filters)
      const consumablesCost = bagCost + filterCost

      await createSpawnBatch({
        code: formData.code || undefined, // Optional batch code for stock entry
        liquid_culture_id: formData.source_type === 'lc' && formData.liquid_culture_id
          ? formData.liquid_culture_id
          : undefined,
        source_syringe_id: formData.source_type === 'syringe' && formData.source_syringe_id
          ? formData.source_syringe_id
          : undefined,
        strain_id: formData.strain_id,
        bag_count: parseInt(formData.bag_count),
        grain_type: formData.grain_type,
        bag_weight_kg: parseFloat(formData.bag_weight_kg),
        use_filters: formData.use_filters,
        spawn_recipe_id: formData.spawn_recipe_id,
        grain_weight_per_bag_kg: formData.grain_weight_per_bag_kg,
        inoculation_date: formData.inoculation_date,
        notes: formData.notes || undefined,
        // Cost fields from preview calculation
        grain_cost: grainCost || 0,
        consumables_cost: consumablesCost || 0,
        total_cost: totalCost || 0,
        cost_per_bag: costPerBag || 0
      })

      // Deduct volume from syringe after successful spawn batch creation
      if (formData.source_type === 'syringe' && formData.source_syringe_id) {
        try {
          await useSyringe(formData.source_syringe_id, parseFloat(formData.syringe_volume_used_ml))
        } catch (syringeErr) {
          console.error('[SpawnBatchForm] Syringe volume deduction error:', syringeErr)
          // Spawn batch was created but syringe deduction failed - warn but don't fail
        }
      }

      // Deduct volume from liquid culture after successful spawn batch creation
      if (formData.source_type === 'lc' && formData.liquid_culture_id && formData.lc_volume_used_ml) {
        try {
          const volumeUsed = parseFloat(formData.lc_volume_used_ml)
          const newRemaining = (selectedLC.volume_remaining_ml || selectedLC.volume_ml) - volumeUsed
          await updateLC(formData.liquid_culture_id, {
            volume_remaining_ml: Math.max(0, newRemaining),
            // Set status to in_use if not already
            status: selectedLC.status === 'ready' ? 'in_use' : selectedLC.status
          })
        } catch (lcErr) {
          console.error('[SpawnBatchForm] LC volume deduction error:', lcErr)
          // Spawn batch was created but LC deduction failed - warn but don't fail
        }
      }

      onSave?.()
    } catch (err) {
      setError(err.message || 'Failed to create spawn batch')
    }
  }

  // Calculate totals for recipe summary
  const bagCount = parseInt(formData.bag_count) || 0
  const totalGrainWeight = selectedRecipe ? Number(selectedRecipe.grain_weight_kg) * bagCount : 0
  const totalBagWeight = selectedRecipe ? Number(selectedRecipe.bag_weight_kg) * bagCount : 0

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Beaker className="h-5 w-5" />
            Create Spawn Batch
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Batch Code - for stock entry */}
          <div className="space-y-2">
            <Label htmlFor="code">Batch Code</Label>
            <Input
              id="code"
              type="text"
              value={formData.code}
              onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
              placeholder="e.g. 260130WO (auto-fills date & strain)"
              className="font-mono"
            />
            <p className="text-xs text-content-tertiary">
              Format: YYMMDD + strain code. Leave blank to auto-generate.
            </p>
          </div>

          {/* Inoculation Date - moved to top */}
          <div className="space-y-2">
            <Label htmlFor="inoculation_date">Inoculation Date</Label>
            <Input
              id="inoculation_date"
              type="date"
              value={formData.inoculation_date}
              onChange={(e) => handleChange('inoculation_date', e.target.value)}
            />
            <p className="text-sm text-content-secondary">
              Expected ready: {expectedReadyDate.toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              })} (14 days)
            </p>
          </div>

          {/* Strain Selection - shown first to enable syringe filtering */}
          <div className="space-y-2">
            <Label htmlFor="strain_id">Strain *</Label>
            <Select
              value={formData.strain_id}
              onValueChange={(value) => handleChange('strain_id', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select strain..." />
              </SelectTrigger>
              <SelectContent>
                {strains?.length === 0 ? (
                  <div className="px-2 py-4 text-center text-content-secondary">
                    No strains available.
                  </div>
                ) : (
                  strains?.map(strain => (
                    <SelectItem key={strain.id} value={strain.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono">{strain.code}</span>
                        <span className="text-content-secondary">
                          - {strain.name}
                        </span>
                      </div>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Source Type Selection */}
          <div className="space-y-3">
            <Label>Inoculation Source (Optional)</Label>
            <RadioGroup
              value={formData.source_type}
              onValueChange={(value) => handleChange('source_type', value)}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="lc" id="source-lc" />
                <Label htmlFor="source-lc" className="cursor-pointer flex items-center gap-1.5">
                  <TestTube className="h-4 w-4 text-content-secondary" />
                  Liquid Culture
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="syringe" id="source-syringe" />
                <Label htmlFor="source-syringe" className="cursor-pointer flex items-center gap-1.5">
                  <Syringe className="h-4 w-4 text-content-secondary" />
                  Culture Syringe
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Source Liquid Culture - shown when LC source type selected */}
          {formData.source_type === 'lc' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="liquid_culture_id">Source Liquid Culture</Label>
                <Select
                  value={formData.liquid_culture_id || 'none'}
                  onValueChange={(value) => handleChange('liquid_culture_id', value === 'none' ? '' : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select liquid culture..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-content-secondary">No LC (stock entry)</span>
                    </SelectItem>
                    {availableCultures
                      .filter(lc => !formData.strain_id || lc.strain_id === formData.strain_id)
                      .map(lc => (
                      <SelectItem key={lc.id} value={lc.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{lc.culture_code}</span>
                          <span className="text-content-secondary">
                            - {lc.strain?.name}
                          </span>
                          <span className="text-xs text-content-tertiary">
                            (G{lc.generation})
                          </span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedLC && (
                  <p className="text-sm text-content-secondary">
                    Strain: <span className="font-medium">{selectedLC.strain?.name}</span>
                    {' • '}Available: <span className="font-medium">{selectedLC.volume_remaining_ml ?? selectedLC.volume_ml}ml</span> of {selectedLC.volume_ml}ml
                  </p>
                )}
              </div>

              {/* Volume to use from LC */}
              {formData.liquid_culture_id && (
                <div className="space-y-2">
                  <Label htmlFor="lc_volume_used_ml">Volume Used (ml)</Label>
                  <div className="relative w-32">
                    <Input
                      id="lc_volume_used_ml"
                      type="number"
                      min={1}
                      max={selectedLC?.volume_remaining_ml || selectedLC?.volume_ml || 1000}
                      step={1}
                      value={formData.lc_volume_used_ml}
                      onChange={(e) => handleChange('lc_volume_used_ml', e.target.value)}
                      placeholder="e.g. 80"
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-tertiary">
                      ml
                    </span>
                  </div>
                  {selectedRecipe && (
                    <p className="text-xs text-content-tertiary">
                      Suggested: {Number(selectedRecipe.lc_volume_ml) * bagCount}ml ({selectedRecipe.lc_volume_ml}ml/bag x {bagCount} bags)
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Source Syringe - shown when syringe source type selected */}
          {formData.source_type === 'syringe' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="source_syringe_id">Source Syringe *</Label>
                {!formData.strain_id ? (
                  <p className="text-sm text-content-secondary py-2">
                    Select a strain first to see available syringes
                  </p>
                ) : availableSyringes.length === 0 ? (
                  <p className="text-sm text-amber-600 py-2">
                    No in-date syringes available for this strain
                  </p>
                ) : (
                  <Select
                    value={formData.source_syringe_id || 'none'}
                    onValueChange={(value) => handleChange('source_syringe_id', value === 'none' ? '' : value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select syringe..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-content-secondary">Select a syringe...</span>
                      </SelectItem>
                      {availableSyringes.map(syringe => (
                        <SelectItem key={syringe.id} value={syringe.id}>
                          <div className="flex items-center gap-2">
                            <span className="font-mono">{syringe.supplier_batch_code}</span>
                            <span className="text-content-secondary">
                              ({syringe.volume_remaining_ml}/{syringe.volume_ml}ml)
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                {selectedSyringe && (
                  <p className="text-sm text-content-secondary">
                    Available: <span className="font-medium">{selectedSyringe.volume_remaining_ml}ml</span> of {selectedSyringe.volume_ml}ml
                    {' • '}Supplier: {selectedSyringe.supplier?.name}
                  </p>
                )}
              </div>

              {/* Volume to use from syringe */}
              {formData.source_syringe_id && (
                <div className="space-y-2">
                  <Label htmlFor="syringe_volume_used_ml">Volume to Use (ml) *</Label>
                  <div className="relative w-32">
                    <Input
                      id="syringe_volume_used_ml"
                      type="number"
                      min={0.1}
                      max={selectedSyringe?.volume_remaining_ml || 100}
                      step={0.1}
                      value={formData.syringe_volume_used_ml}
                      onChange={(e) => handleChange('syringe_volume_used_ml', e.target.value)}
                      placeholder="0.0"
                      className="pr-8"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-content-tertiary">
                      ml
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Spawn Recipe Selector */}
          <div className="space-y-2">
            <Label>Spawn Recipe *</Label>
            <Select
              value={formData.spawn_recipe_id || 'none'}
              onValueChange={handleRecipeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select spawn recipe..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-content-secondary">Select a recipe...</span>
                </SelectItem>
                {recipes.map(recipe => (
                  <SelectItem key={recipe.id} value={recipe.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{recipe.name}</span>
                      <span className="text-xs text-content-tertiary">
                        ({recipe.grain_weight_kg}kg {recipe.grain_type} + {recipe.water_weight_kg}kg water)
                      </span>
                    </div>
                  </SelectItem>
                ))}
                <SelectItem value="__new__">
                  <div className="flex items-center gap-2 text-brand-primary">
                    <Plus className="h-3.5 w-3.5" />
                    <span>Add Spawn Recipe</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Number of Bags */}
          <div className="space-y-2">
            <Label htmlFor="bag_count">Number of Bags *</Label>
            <Input
              id="bag_count"
              type="number"
              min={1}
              max={100}
              value={formData.bag_count}
              onChange={(e) => handleChange('bag_count', e.target.value)}
              className="w-24"
            />
          </div>

          {/* Recipe Summary Panel */}
          {selectedRecipe && bagCount > 0 && (
            <div className="rounded-md bg-surface-secondary p-3 space-y-2">
              <div className="text-xs text-content-tertiary uppercase tracking-wide font-medium">
                Recipe Summary
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-content-secondary">Grain per bag:</span>
                <span className="font-medium">{selectedRecipe.grain_weight_kg} kg {selectedRecipe.grain_type}</span>
                <span className="text-content-secondary">Water per bag:</span>
                <span className="font-medium">{selectedRecipe.water_weight_kg} kg</span>
                <span className="text-content-secondary">Bag size:</span>
                <span className="font-medium">{selectedRecipe.bag_weight_kg} kg</span>
                <span className="text-content-secondary">LC per bag:</span>
                <span className="font-medium">{selectedRecipe.lc_volume_ml} ml</span>
                <span className="text-content-secondary">Filters:</span>
                <span className="font-medium">{selectedRecipe.use_filters ? 'Yes' : 'No'}</span>
              </div>
              <div className="border-t pt-2 mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                <span className="text-content-secondary">Total grain ({bagCount} bags):</span>
                <span className="text-lg font-bold">{totalGrainWeight.toFixed(1)} kg</span>
                <span className="text-content-secondary">Total bag weight:</span>
                <span className="font-medium">{totalBagWeight.toFixed(1)} kg</span>
              </div>
            </div>
          )}

          {/* Cost Preview */}
          <SpawnCostPreview formValues={formData} />

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="Any observations or special handling notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.strain_id || !formData.spawn_recipe_id}>
              {saving ? 'Creating...' : 'Create Spawn Batch'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>

      {/* Nested Recipe Modal */}
      <SpawnRecipeModal
        open={recipeModalOpen}
        onOpenChange={setRecipeModalOpen}
        onSave={handleCreateRecipe}
        saving={savingRecipe}
      />
    </Dialog>
  )
}
